/**
 * Dice tray: the pending roll plus the manipulations that can bend it.
 *
 * Every manipulation shows its Fate Debt cost up front. GDD §30 lists "the dice
 * feel like pure RNG" as a headline risk; showing the price of control is what
 * turns a roll into a decision rather than an outcome.
 */

import {
  FATE_DEBT_COST,
  ManipulationError,
  isUnlocked,
  type GameSession,
  type ManipulationKind,
} from '@lastroll/engine';
import { dieUri } from './art.js';

const BAND_LABEL: Record<string, string> = {
  failure: '실패',
  ordinary: '보통',
  strong: '강한 결과',
  empowered: '스킬 강화',
  critical: '크리티컬',
};

const MANIP_LABEL: Record<ManipulationKind, string> = {
  reroll: '다시 굴리기',
  hold: '보관',
  split: '쪼개기',
  double: '두 배',
  sacrifice: '희생',
  gamble: '도박',
  bank: '저축',
  spend: '사용',
};

const MANIP_HINT: Record<ManipulationKind, string> = {
  reroll: '눈을 버리고 다시 굴린다',
  hold: '이 눈을 나중에 쓰려고 보관한다',
  split: '짝수 4 이상을 반으로 나눈다',
  double: '두 배로. 대신 빚이 크다',
  sacrifice: '지금 이득을 포기하고 더 큰 것을 얻는다',
  gamble: '더 좋아지거나 더 나빠진다',
  bank: '장면을 넘겨 보관한다',
  spend: '보관한 눈을 쓴다',
};

/** Ordered so the tutorial three come first (GDD §30: reveal gradually). */
const ORDER: ManipulationKind[] = ['reroll', 'hold', 'split', 'double', 'gamble', 'sacrifice', 'bank'];

export interface DiceTrayProps {
  session: GameSession;
  rolling: boolean;
  freeRerolls: number;
  onManipulate: (kind: ManipulationKind) => void;
  /** Strip layout, used while the board is the focus of the screen. */
  compact?: boolean;
}

export function DiceTray({
  session,
  rolling,
  freeRerolls,
  onManipulate,
  compact = false,
}: DiceTrayProps): JSX.Element | null {
  const record = session.pendingRoll;
  const die = session.currentDie;
  if (!record) return null;

  const canSplit = record.finalFace % 2 === 0 && record.finalFace >= 4;
  const bankFull = session.state.dice.bank.length >= session.state.dice.bankCapacity;

  return (
    <div className={`tray${compact ? ' tray--compact' : ''}`}>
      <div className={`die${rolling ? ' is-rolling' : ''}`}>
        <img
          src={dieUri(die.skin, record.finalFace, record.band === 'critical')}
          alt={`${record.finalFace}이(가) 나왔다`}
        />
      </div>

      <div style={{ textAlign: 'center' }}>
        <div className="tray__face" aria-live="polite">
          {record.finalFace}
        </div>
        <div className={`tray__band band--${record.band}`}>{BAND_LABEL[record.band]}</div>
        {record.splitInto.length > 0 && (
          <div className="muted">→ {record.splitInto.join(' + ')}로 나뉨</div>
        )}
        {record.manipulations.length > 0 && (
          <div className="muted">
            자연값 {record.naturalFace} · {record.manipulations.map((m) => MANIP_LABEL[m]).join(' → ')}
          </div>
        )}
      </div>

      <div className="manips">
        {ORDER.filter((kind) => isUnlocked(session.state.dice, kind)).map((kind) => {
          const free = kind === 'reroll' && freeRerolls > 0;
          const disabled =
            (kind === 'split' && !canSplit) ||
            ((kind === 'hold' || kind === 'bank') && bankFull);
          const cost = FATE_DEBT_COST[kind];
          return (
            <button
              key={kind}
              type="button"
              className={`manip${free ? ' manip--free' : ''}`}
              disabled={disabled}
              title={MANIP_HINT[kind]}
              onClick={() => onManipulate(kind)}
            >
              {MANIP_LABEL[kind]}
              <span className="manip__cost">{free ? '무료' : cost > 0 ? `빚 +${cost}` : '빚 없음'}</span>
            </button>
          );
        })}
      </div>
      {freeRerolls > 0 && <div className="muted">무료 재굴림 {freeRerolls}회</div>}
    </div>
  );
}

export { ManipulationError };
